import { useState } from "react";
import FaqItem from "./FaqItem";
import faqHero from "../../assets/faq-hero.png";
import {
  LayoutGrid,
  User,
  Users,
  Luggage,
  ShieldCheck,
  Wallet,
  Compass,
  Headset,
  Phone,
} from "lucide-react";

const categories = [
  { id: "general", name: "General", icon: LayoutGrid },
  { id: "account", name: "Account & Profile", icon: User },
  { id: "matching", name: "Matching & Connections", icon: Users },
  { id: "trips", name: "Trips & Travel", icon: Luggage },
  { id: "safety", name: "Safety & Security", icon: ShieldCheck },
  { id: "payments", name: "Payments & Plans", icon: Wallet },
  { id: "explore", name: "Explore", icon: Compass },
  { id: "support", name: "Technical Support", icon: Headset },
];

const faqData = [
  {
    category: "general",
    question: "What is SoloConnect?",
    answer: (
      <p>
        SoloConnect is a travel companion platform that helps solo travelers
        connect with people traveling to the same destination on similar dates.
        Chat, plan, and choose the right co-traveler before your journey begins.
      </p>
    ),
  },
  {
    category: "general",
    question: "How does SoloConnect work?",
    answer: (
      <>
        <p className="mb-2">SoloConnect works in simple steps:</p>
        <ol className="list-decimal list-inside space-y-1 mb-2">
          <li>Create your profile and add your travel details.</li>
          <li>Browse travelers going to the same destination.</li>
          <li>Send a trip request to travelers you'd like to connect with.</li>
          <li>Once accepted, start chatting and plan your trip together.</li>
          <li>Select your co-traveler and enjoy a safe, social journey.</li>
        </ol>
      </>
    ),
  },
  {
    category: "general",
    question: "Is SoloConnect a dating app?",
    answer: (
      <p>
        No. SoloConnect is strictly a travel companion app designed to help solo
        travelers find like-minded people heading to the same destination. It is
        not a dating platform, and any misuse of the platform is strictly
        prohibited.
      </p>
    ),
  },
  {
    category: "payments",
    question: "Is SoloConnect free to use?",
    answer: (
      <>
        <p className="mb-2">
          SoloConnect offers both a free plan and a premium Elite plan.
        </p>
        <p className="mb-2">The free plan includes basic features such as:</p>
        <ul className="list-disc list-inside space-y-1 mb-2">
          <li>Creating and browsing trips</li>
          <li>Sending trip requests</li>
          <li>Basic chat after request acceptance</li>
        </ul>
        <p>
          The Elite plan unlocks additional features like Instant Connect, Trip
          Boost, Profile Boost, and more.
        </p>
      </>
    ),
  },
  {
    category: "general",
    question: "Who can use SoloConnect?",
    answer: (
      <p>
        SoloConnect is available to anyone aged 18 and above who enjoys solo
        travel and is looking for a trusted travel companion. All users must
        agree to our Terms of Service and Community Guidelines.
      </p>
    ),
  },
  {
    category: "account",
    question: "How do I sign up for SoloConnect?",
    answer: (
      <>
        <p className="mb-2">To sign up:</p>
        <ol className="list-decimal list-inside space-y-1 mb-2">
          <li>
            Download the SoloConnect app from the App Store or Google Play.
          </li>
          <li>Open the app and tap Sign Up.</li>
          <li>Enter your name, email address, and create a password.</li>
          <li>Verify your email address.</li>
          <li>
            Complete your profile with travel preferences and a profile photo.
          </li>
        </ol>
      </>
    ),
  },
  {
    category: "account",
    question: "Is my personal data safe on SoloConnect?",
    answer: (
      <p>
        Yes. SoloConnect takes data privacy seriously. We collect only the data
        necessary to provide our services and handle it in accordance with our
        Privacy Policy. We do not sell your personal data to third parties.
      </p>
    ),
  },
  {
    category: "account",
    question: "Can I use SoloConnect without creating a profile?",
    answer: (
      <p>
        No. A complete profile is required to use SoloConnect. This ensures all
        users can make informed decisions when choosing a travel companion and
        helps build a trustworthy community.
      </p>
    ),
  },
  {
    category: "trips",
    question: "1. How do I create a trip?",
    answer: (
      <>
        <p className="mb-2">To create a trip:</p>
        <ol className="list-decimal list-inside space-y-1 mb-2">
          <li>Open the Home screen.</li>
          <li>Tap the "+" (Create Trip) button.</li>
          <li>Enter your destination, travel dates, and trip details.</li>
          <li>Review the information.</li>
          <li>Tap Create Trip.</li>
        </ol>
        <p>
          Your trip will become visible to other travelers with similar travel
          plans, allowing them to discover and connect with you.
        </p>
      </>
    ),
  },
  {
    category: "matching",
    question: "2. How do I send a trip request?",
    answer: (
      <>
        <p className="mb-2">To send a trip request:</p>
        <ol className="list-decimal list-inside space-y-1 mb-2">
          <li>Browse travelers going to your destination.</li>
          <li>Open the traveler's profile.</li>
          <li>Tap Send Request.</li>
          <li>Wait for the traveler to accept or reject your request.</li>
        </ol>
        <p>
          Once your request is accepted, you can begin chatting (or use Instant
          Connect if eligible).
        </p>
      </>
    ),
  },
  {
    category: "matching",
    question: "3. How does chat work?",
    answer: (
      <>
        <p className="mb-2">Chat becomes available after:</p>
        <ul className="list-disc list-inside space-y-1 mb-2">
          <li>Your trip request is accepted, or</li>
          <li>You use Instant Connect (Premium feature), where applicable.</li>
        </ul>
        <p className="mb-2">
          Each trip has its own dedicated chat, allowing you to manage
          conversations separately for different trips.
        </p>
        <p>
          Please communicate respectfully and avoid sharing sensitive personal
          or financial information.
        </p>
      </>
    ),
  },
  {
    category: "matching",
    question: "4. How do I select a co-traveler?",
    answer: (
      <>
        <p className="mb-2">After chatting with one or more travelers:</p>
        <ol className="list-decimal list-inside space-y-1 mb-2">
          <li>Open the relevant trip chat.</li>
          <li>Tap Select Co-Traveler.</li>
          <li>Choose the traveler you wish to travel with.</li>
          <li>Confirm your selection.</li>
        </ol>
        <p className="mb-2">
          Once confirmed, the selected traveler will receive a notification
          informing them that they have been chosen as your co-traveler.
        </p>
        <p>
          If you later decide to travel with someone else, you can change your
          selection before your trip begins. The previously selected traveler
          will be notified that they are no longer your selected co-traveler,
          and the newly selected traveler will receive a confirmation
          notification.
        </p>
      </>
    ),
  },
  {
    category: "matching",
    question: "5. What is Instant Connect?",
    answer: (
      <>
        <p className="mb-2">
          Instant Connect is an exclusive feature available with the SoloConnect
          Elite plan.
        </p>
        <p className="mb-2">
          It allows Elite members to start a conversation with another traveler
          immediately without waiting for their trip request to be accepted,
          helping you connect faster and plan your trip more efficiently.
        </p>
        <p>
          Availability of Instant Connect is subject to your active Elite
          subscription.
        </p>
      </>
    ),
  },
  {
    category: "matching",
    question: "6. What happens if my request is rejected?",
    answer: (
      <>
        <p className="mb-2">If your request is rejected:</p>
        <ul className="list-disc list-inside space-y-1 mb-2">
          <li>You will be notified.</li>
          <li>The traveler will not be added to your chat list.</li>
          <li>
            You can continue exploring and send requests to other compatible
            travelers.
          </li>
        </ul>
        <p>Rejected requests do not affect your ability to use SoloConnect.</p>
      </>
    ),
  },
  {
    category: "safety",
    question: "7. How does Safe Mode work?",
    answer: (
      <>
        <p className="mb-2">
          Safe Mode is designed to improve your experience by limiting
          interactions to users who meet SoloConnect's verification
          requirements.
        </p>
        <p>
          While Safe Mode increases trust, it does not guarantee the identity,
          intentions, or behavior of other users. Always exercise caution when
          interacting or meeting someone.
        </p>
      </>
    ),
  },
  {
    category: "safety",
    question: "8. How do I use the SOS feature?",
    answer: (
      <>
        <p className="mb-2">If you need emergency assistance:</p>
        <ol className="list-decimal list-inside space-y-1 mb-2">
          <li>Open the SOS feature within the app.</li>
          <li>
            Tap the emergency call option to contact your local emergency
            service.
          </li>
        </ol>
        <p className="mb-2">
          If you have added Trusted Contacts, your phone's default SMS
          application will open with:
        </p>
        <ul className="list-disc list-inside space-y-1 mb-3">
          <li>A pre-filled emergency message.</li>
          <li>Your current GPS coordinates.</li>
        </ul>
        <p className="mb-2">
          Review the message and tap Send to notify your Trusted Contacts.
        </p>
        <p>
          SoloConnect does not automatically send emergency messages or
          continuously track your location.
        </p>
      </>
    ),
  },
  {
    category: "safety",
    question: "9. How do I report a user?",
    answer: (
      <>
        <p className="mb-2">If you encounter inappropriate behavior:</p>
        <ol className="list-decimal list-inside space-y-1 mb-3">
          <li>Open the user's profile or chat.</li>
          <li>Tap the Report option.</li>
          <li>Select the reason for your report.</li>
          <li>Add additional details if prompted.</li>
          <li>Submit the report.</li>
        </ol>
        <p>
          Our moderation team will review reported content and take appropriate
          action where necessary.
        </p>
      </>
    ),
  },
  {
    category: "account",
    question: "10. How do I delete my account?",
    answer: (
      <>
        <p className="mb-2">To permanently delete your account:</p>
        <ol className="list-decimal list-inside space-y-1 mb-3">
          <li>Open Settings.</li>
          <li>Tap Delete Account.</li>
          <li>Review the information provided.</li>
          <li>Confirm your decision.</li>
        </ol>
        <p>
          Deleting your account permanently removes your access to SoloConnect.
          Your personal data will be deleted or anonymized in accordance with
          our Privacy Policy, unless retention is required by law or for
          legitimate safety, fraud prevention, or dispute resolution purposes.
        </p>
      </>
    ),
  },
  {
    category: "trips",
    question: "11. Can I edit or delete my trip after creating it?",
    answer: (
      <>
        <p className="mb-2">
          Once a trip has been created, it cannot be edited. This helps ensure
          consistency for travelers who have already discovered your trip or
          sent requests based on your original travel details.
        </p>
        <p className="mb-2">If your travel plans change, you can:</p>
        <ul className="list-disc list-inside space-y-1 mb-3">
          <li>Delete the existing trip.</li>
          <li>
            Create a new trip with the updated destination, dates, or travel
            details.
          </li>
        </ul>
        <p>
          Please note that deleting a trip will remove all associated requests
          and conversations related to that trip.
        </p>
      </>
    ),
  },
  {
    category: "safety",
    question: "12. Can I block another user?",
    answer: (
      <p>
        Yes. You can block a user from their profile or chat. Once blocked, they
        will no longer be able to contact you through SoloConnect unless you
        choose to unblock them later.
      </p>
    ),
  },
  {
    category: "account",
    question: "13. What does the Verified Traveler badge mean?",
    answer: (
      <p>
        A Verified Traveler badge indicates that the user has completed
        additional verification steps requested by SoloConnect. While
        verification helps build trust, it does not guarantee a user's identity,
        intentions, or conduct.
      </p>
    ),
  },
  {
    category: "explore",
    question: "14. What are Trip Boost and Profile Boost?",
    answer: (
      <p>
        Trip Boost increases the visibility of a specific trip, while Profile
        Boost increases the visibility of your profile for a limited time. These
        features help improve discoverability but do not guarantee matches or
        connections.
      </p>
    ),
  },
  {
    category: "trips",
    question: "15. Can I have multiple trips at the same time?",
    answer: (
      <>
        <p className="mb-2">
          Yes. You can create multiple trips for different destinations or
          travel dates.
        </p>
        <p className="mb-2">Each trip has its own:</p>
        <ul className="list-disc list-inside space-y-1 mb-3">
          <li>Traveler matches</li>
          <li>Trip requests</li>
          <li>Chat conversations</li>
          <li>Co-traveler selection</li>
        </ul>
        <p>
          This allows you to manage every journey independently without mixing
          conversations or requests across different trips.
        </p>
      </>
    ),
  },
];

export default function FaqSection({ heroImage }) {
  const [openIndex, setOpenIndex] = useState(0);
  const [activeCategory, setActiveCategory] = useState("general");
  // Use the custom image passed via prop, or fall back to the default bundled hero
  const heroSrc = heroImage || faqHero;

  // Only show questions that belong to the selected category
  const filteredFaqs = faqData.filter(
    (item) => item.category === activeCategory,
  );

  const toggleIndex = (index) => {
    setOpenIndex((prev) => (prev === index ? -1 : index));
  };

  const handleCategoryClick = (id) => {
    setActiveCategory(id);
    setOpenIndex(0); // reset the open accordion item whenever the category changes
  };

  return (
    <main className="flex-1 flex flex-col bg-white">
      {/* Hero Section */}
      <div className="w-full bg-[#F9F6FE] flex flex-col md:flex-row items-center justify-between px-6 sm:px-12 md:px-24 py-8 md:py-12 lg:py-24">
       {/* Text Content */}
<div className="md:w-1/2 flex flex-col justify-center text-left mb-10 md:mb-0 z-10 relative">
  <h1 className="font-extrabold tracking-tight leading-[1.08]">
    <span className="block whitespace-nowrap text-[7vw] sm:text-[6vw] md:text-[4.2vw] lg:text-[4vw] text-black">
      Frequently Asked
    </span>
    <span className="block whitespace-nowrap text-[7vw] sm:text-[6vw] md:text-[4.2vw] lg:text-[4vw] text-[#7828C8]">
      Questions
    </span>
  </h1>
  <p className="mt-4 text-gray-600 text-[15px] md:text-base leading-relaxed max-w-lg ">
    Find answers to the most common questions about SoloConnect and how
    it works.
  </p>
</div>

        {/* Image Content */}
        <div className="md:w-1/2 flex justify-center md:justify-end relative">
          {/* Default/Prop image goes here */}
          <img
            src={heroSrc}
            alt="FAQ Illustration"
            className="w-full max-w-[300px] sm:max-w-[400px] md:max-w-[500px] lg:max-w-[600px] object-contain"
          />
        </div>
      </div>

      {/* Hide scrollbar on the mobile pill menu */}
      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* FAQ Main Content */}
      <div className="w-full max-w-6xl mx-auto px-4 sm:px-8 py-8 md:py-20 flex flex-col lg:flex-row gap-4 lg:gap-12">
        {/* Sidebar */}
        <div className="w-full lg:w-1/3 xl:w-1/4 lg:sticky lg:top-24 h-max">
          {/* Mobile: horizontal scrollable pills */}
          <div className="flex lg:hidden gap-2 overflow-x-auto no-scrollbar -mx-4 px-4 pb-1">
            {categories.map((cat) => {
              const Icon = cat.icon;
              const isActive = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryClick(cat.id)}
                  className={`flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap border ${
                    isActive
                      ? "bg-[#f3f0fb] text-[#7828C8] border-[#e4d9f7]"
                      : "bg-white text-gray-800 border-gray-200"
                  }`}
                >
                  <Icon
                    size={16}
                    className={isActive ? "text-[#7828C8]" : "text-gray-800"}
                  />
                  {cat.name}
                </button>
              );
            })}
          </div>

          {/* Desktop: vertical stacked list */}
          <div className="hidden lg:flex flex-col space-y-2">
            {categories.map((cat) => {
              const Icon = cat.icon;
              const isActive = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryClick(cat.id)}
                  className={`w-full flex items-center gap-2 xl:gap-3 px-3 xl:px-5 py-4 rounded-xl text-left font-medium border whitespace-nowrap ${
                    isActive
                      ? "bg-[#f3f0fb] text-[#7828C8] border-[#e4d9f7]"
                      : "bg-white text-gray-800 border-gray-200"
                  }`}
                >
                  <Icon
                    size={18}
                    className={`shrink-0 ${isActive ? "text-[#7828C8]" : "text-gray-800"}`}
                  />
                  <span className="truncate text-[13px] xl:text-sm">
                    {cat.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* FAQ List */}
        <div className="w-full lg:w-2/3 xl:w-3/4 flex flex-col space-y-3 sm:space-y-4">
          {filteredFaqs.length > 0 ? (
            filteredFaqs.map((item, index) => (
              <FaqItem
                key={item.question}
                question={item.question}
                answer={item.answer}
                isOpen={openIndex === index}
                onToggle={() => toggleIndex(index)}
              />
            ))
          ) : (
            <div className="text-gray-500 text-sm px-2 py-8 text-center border border-dashed border-gray-200 rounded-xl">
              No FAQs available in this category yet.
            </div>
          )}
        </div>
      </div>

      {/* Support Banner — MOBILE */}
      <div className="block md:hidden w-full px-4 pb-12">
        <div
          className="rounded-[32px] overflow-hidden relative flex flex-col items-center text-center"
          style={{
            backgroundImage: "url('/img/faq/phonebgfq.svg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            backgroundColor: "#F0EBFB",
          }}
        >
          {/* Illustration */}
          <div className="w-full flex justify-center pt-8 pb-2 relative z-10">
            <img
              src="/img/faq/imgfq.svg"
              alt="Support Illustration"
              className="w-48 object-contain"
            />
          </div>

          {/* Text + Button */}
          <div className="flex flex-col items-center px-6 pb-8 relative z-10">
            <h2 className="text-xl font-bold text-black mb-2">
              Still have questions?
            </h2>
            <p className="text-gray-700 text-sm mb-6 leading-relaxed max-w-xs">
              We're here to help! Reach out to our support team and we'll get
              back to you as soon as possible.
            </p>
            <button className="no-scale bg-[#6B1AC4] text-white text-sm font-semibold px-8 py-3 rounded-full flex items-center gap-2 shadow-sm">
              <Phone size={18} />
              Contact Support
            </button>
          </div>
        </div>
      </div>

      {/* Support Banner — MOBILE */}
      <div className="block md:hidden w-full px-4 pb-12">
        <div className="rounded-[28px] relative flex flex-col items-center text-center bg-[#F1ECFC] px-6 pt-8 pb-8">
          <img
            src="/img/faq/imgfq.svg"
            alt="Support Illustration"
            className="w-44 object-contain mb-6"
          />
          <h2 className="text-xl font-bold text-black mb-2">
            Still have questions?
          </h2>
          <p className="text-gray-600 text-sm mb-6 leading-relaxed max-w-xs">
            We're here to help! Reach out to our support team and we'll get back
            to you as soon as possible.
          </p>
          <button className="no-scale bg-[#6B1AC4] text-white text-sm font-semibold px-8 py-3 rounded-full flex items-center gap-2 shadow-sm">
            <Phone size={18} />
            Contact Support
          </button>
        </div>
      </div>

      {/* Support Banner — DESKTOP */}
      <div className="hidden md:block w-full max-w-6xl mx-auto px-8 pb-20">
        <div className="rounded-[28px] bg-[#F1ECFC] flex flex-row items-center gap-10 px-10 py-8">
          <div className="flex-shrink-0">
            <img
              src="/img/faq/imgfq.svg"
              alt="Support Illustration"
              className="w-56 lg:w-64 object-contain"
            />
          </div>
          <div className="flex flex-col items-start text-left">
            <h2 className="text-2xl sm:text-3xl font-bold text-black mb-3">
              Still have questions?
            </h2>
            <p className="text-gray-600 text-sm sm:text-base mb-6 max-w-md leading-relaxed">
              We're here to help! Reach out to our support team and we'll get
              back to you as soon as possible.
            </p>
            <button className="no-scale bg-[#6B1AC4] text-white text-sm font-semibold px-8 py-3 rounded-full flex items-center gap-2 shadow-sm">
              <Phone size={18} />
              Contact Support
            </button>
          </div>
        </div>
      </div>
    </main>
  );
}
